import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GoalsTable } from "./GoalsTable";
import { GoalsTemplateCarousel } from "./templates/GoalsTemplateCarousel";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PerformanceGoal } from "./services/performanceService";
import { GoalTemplate } from "./templates/goalTemplates";
import { useSupervisorSync } from "./hooks/useSupervisorSync";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface TeamMember {
  id: string;
  full_name: string;
}

const ManageTeamMembersTab = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>("");
  const [memberGoals, setMemberGoals] = useState<PerformanceGoal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);
  const { activeYear } = usePerformanceYear();

  // Sync supervisor_id for all team member goals (current user is the supervisor)
  useSupervisorSync(memberGoals, selectedMember);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  useEffect(() => {
    if (selectedMember) {
      loadMemberGoals(selectedMember);
    } else {
      setMemberGoals([]);
    }
  }, [selectedMember, activeYear]);

  const loadTeamMembers = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Error getting user:", userError);
        return;
      }

      const { data: members, error } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('supervisor_id', user.id);

      if (error) {
        console.error("Error loading team members:", error);
        toast.error("Failed to load team members");
        return;
      }

      const sortedMembers = (members || []).sort((a, b) => 
        (a.full_name || a.id).localeCompare(b.full_name || b.id)
      );
      
      setTeamMembers(sortedMembers);
      
      if (sortedMembers.length > 0 && !selectedMember) {
        setSelectedMember(sortedMembers[0].id);
      }
    } catch (error) {
      console.error("Error loading team members:", error);
      toast.error("Failed to load team members");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMemberGoals = async (memberId: string) => {
    setIsLoadingGoals(true);
    try {
      const { data: goals, error } = await supabase
        .from('performance_goals')
        .select('*')
        .eq('user_id', memberId)
        .eq('year', activeYear);

      if (error) {
        console.error("Error loading member goals:", error);
        toast.error("Failed to load member goals");
        return;
      }

      const sortedGoals = (goals || []).sort((a, b) => a.goal_title.localeCompare(b.goal_title)) as PerformanceGoal[];
      setMemberGoals(sortedGoals);
    } catch (error) {
      console.error("Error loading member goals:", error);
      toast.error("Failed to load member goals");
    } finally {
      setIsLoadingGoals(false);
    }
  };

  const handleUseTemplate = async (template: GoalTemplate) => {
    if (!selectedMember) {
      toast.error("Please select a team member first");
      return;
    }

    try {
      const goalsToInsert = template.goals.map(goal => ({
        user_id: selectedMember,
        goal_title: goal.goal_title,
        goal_description: goal.goal_description,
        goal_category: goal.goal_category,
        goal_type: goal.goal_type,
        target_value: null,
        current_progress: 0,
        status: 'active' as const,
        start_date: new Date().toISOString().split('T')[0],
        target_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        year: activeYear
      }));

      const { error } = await supabase
        .from('performance_goals')
        .insert(goalsToInsert);

      if (error) {
        console.error("Error inserting goals from template:", error);
        toast.error("Failed to add goals from template");
        return;
      }

      toast.success(`Successfully added ${template.goals.length} goals from ${template.role} template`);
      
      refreshGoals();
    } catch (error) {
      console.error("Error using template:", error);
      toast.error("Failed to add goals from template");
    }
  };

  const refreshGoals = () => {
    if (selectedMember) {
      loadMemberGoals(selectedMember);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading team members...</div>
      </div>;
  }

  if (teamMembers.length === 0) {
    return <div className="text-center py-8">
        <p className="text-gray-400">No team members found. You need to have team members assigned to you to manage their goals.</p>
      </div>;
  }

  const totalGoals = memberGoals.length;
  const activeGoals = memberGoals.filter(goal => goal.status === 'active').length;
  const completedGoals = memberGoals.filter(goal => goal.status === 'completed').length;
  const averageProgress = memberGoals.length > 0 ? Math.round(memberGoals.reduce((sum, goal) => sum + goal.current_progress, 0) / memberGoals.length) : 0;

  const selectedMemberName = teamMembers.find(member => member.id === selectedMember)?.full_name || "Unknown";

  const areGoalsFrozen = false;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <h3 className="text-lg font-semibold text-white">Select Team Member:</h3>
        <Select value={selectedMember} onValueChange={setSelectedMember}>
          <SelectTrigger className="w-full sm:w-64 bg-white text-black border-primary">
            <SelectValue placeholder="Choose a team member" />
          </SelectTrigger>
          <SelectContent>
            {teamMembers.map((member) => (
              <SelectItem key={member.id} value={member.id}>
                {member.full_name || member.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedMember && (
        <div className="space-y-4">
          
          <GoalsTemplateCarousel 
            onUseTemplate={handleUseTemplate}
            isDisabled={areGoalsFrozen}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/30 border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-white flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-blue-400">{totalGoals}</div>
                    <span className="text-[13px]">Total Goals</span>
                  </div>
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/50 text-xs">
                    {activeGoals} active
                  </Badge>
                </CardTitle>
              </CardHeader>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/30 border-green-500/30 hover:shadow-lg hover:shadow-green-500/25 transition-all duration-300">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-white flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-green-400">{activeGoals}</div>
                    <span className="text-[13px]">Active Goals</span>
                  </div>
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/50 text-xs">
                    In progress
                  </Badge>
                </CardTitle>
              </CardHeader>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/30 border-purple-500/30 hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-white flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-purple-400">{averageProgress}%</div>
                    <span className="text-[13px]">On Track</span>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/50 text-xs">
                    Avg progress
                  </Badge>
                </CardTitle>
              </CardHeader>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/30 border-yellow-500/30 hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-300">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-white flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-yellow-400">{completedGoals}</div>
                    <span className="text-[13px]">Completed</span>
                  </div>
                  <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/50 text-xs">
                    Achieved
                  </Badge>
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {isLoadingGoals ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-primary animate-pulse">Loading goals...</div>
            </div>
          ) : (
            <div className="border-2 border-primary rounded-lg">
              <GoalsTable goals={memberGoals} onRefresh={refreshGoals} selectedTeamMemberId={selectedMember} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageTeamMembersTab;
